import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Stack,
} from "@mui/material";
import { ImageFill } from "../image-fill";

const MAX_TITLE_LENGTH = 50;
const MAX_DESCRIPTION_LENGTH = 200;
const MAX_CREDIT_LENGTH = 100;

interface ImageDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (title: string, credit: string, description: string) => void;
  imageUrl?: string;
  currentTitle?: string;
  currentDescription?: string;
  currentCredit?: string;
}

export const ImageDetailsDialog: React.FC<ImageDetailsDialogProps> = ({
  open,
  onClose,
  onSave,
  imageUrl,
  currentTitle = "",
  currentDescription = "",
  currentCredit = "",
}) => {
  const [title, setTitle] = useState(currentTitle);
  const [credit, setCredit] = useState(currentCredit);
  const [description, setDescription] = useState(currentDescription);

  useEffect(() => {
    if (open) {
      setTitle(currentTitle);
      setCredit(currentCredit);
      setDescription(currentDescription);
    }
  }, [open, currentTitle, currentDescription, currentCredit]);

  const handleSave = () => {
    onSave(title.trim(), credit.trim(), description.trim());
    onClose();
  };

  const handleCancel = () => {
    setTitle(currentTitle);
    setCredit(currentCredit);
    setDescription(currentDescription);
    onClose();
  };

  const titleRemaining = MAX_TITLE_LENGTH - title.length;
  const creditRemaining = MAX_CREDIT_LENGTH - credit.length;
  const descriptionRemaining = MAX_DESCRIPTION_LENGTH - description.length;

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle sx={{ fontWeight: 600, pb: 2 }}>
        Edit Image Details
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3}>
          {/* Image Preview */}
          {imageUrl && (
            <Box
              sx={{
                width: "100%",
                height: 200,
                borderRadius: 2,
                overflow: "hidden",
                backgroundColor: "#F5F5F5",
              }}
            >
              <ImageFill
                src={imageUrl}
                sx={{
                  width: "100%",
                  height: "100%",
                }}
              />
            </Box>
          )}

          {/* Title Field */}
          <TextField
            label="Image Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            inputProps={{
              maxLength: MAX_TITLE_LENGTH,
            }}
            helperText={`${titleRemaining} characters remaining`}
            FormHelperTextProps={{
              sx: {
                textAlign: "right",
                marginLeft: 0,
                marginRight: 0,
              },
            }}
          />

          {/* Credit Field */}
          <TextField
            label="Image Credit"
            value={credit}
            onChange={(e) => setCredit(e.target.value)}
            fullWidth
            inputProps={{
              maxLength: MAX_CREDIT_LENGTH,
            }}
            helperText={`${creditRemaining} characters remaining`}
            FormHelperTextProps={{
              sx: {
                textAlign: "right",
                marginLeft: 0,
                marginRight: 0,
              },
            }}
          />

          {/* Description Field */}
          <TextField
            label="Image Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={4}
            inputProps={{
              maxLength: MAX_DESCRIPTION_LENGTH,
            }}
            helperText={`${descriptionRemaining} characters remaining`}
            FormHelperTextProps={{
              sx: {
                textAlign: "right",
                marginLeft: 0,
                marginRight: 0,
              },
            }}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
        <Button onClick={handleCancel} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          sx={{
            backgroundColor: "#00A06D",
            "&:hover": {
              backgroundColor: "#008A5C",
            },
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
